/**
 * „powered by": Zu welchem Showcase gehört eine Antwort von K4ia — und hat
 * er einen Partner, der darunter stehen soll? Eine Regel für den gewöhnlichen
 * Chat, den Intro-Chat und den Intro-Bildschirm.
 *
 * Eine Antwort gehört zu einem Showcase, wenn
 * - K4ia ihn dafür geladen hat (Werkzeug `get_showcase`), oder
 * - die Frage davor ihn ausdrücklich nennt, so wie die App sie selbst
 *   formuliert: „Ich stehe vor: <Titel>" (NFC-Tag, Kamera, Foto, Rundgang)
 *   und „Erzähl mir von: <Titel>" (Vorschläge).
 *
 * Erzählt K4ia aus der Wissenssuche, ohne den Showcase zu laden und ohne
 * dass er in der Frage stand, steht kein Hinweis — lieber einmal zu wenig
 * als bei der falschen Antwort.
 */

export interface PartnerShowcase {
  id: string
  slug: string
  title: string
  partnerName: string
  partnerLogoKey: string | null
}

/**
 * Ein Showcase zeigt „powered by", sobald ein Partnername **oder** ein Logo
 * gepflegt ist. Anfangs zählte nur der Name — und ein hochgeladenes Logo
 * blieb unsichtbar, weil der Name nicht gespeichert war (The Wall, 02.10.).
 */
export function hatPartner(s: Pick<PartnerShowcase, 'partnerName' | 'partnerLogoKey'>): boolean {
  return s.partnerName.trim() !== '' || Boolean(s.partnerLogoKey)
}

/** Adresse des Logos in der Besucher-App (`/media/<Schlüssel>`, routes/media.ts). */
export function partnerLogoPfad(key: string): string {
  return `/media/${key.split('/').map(encodeURIComponent).join('/')}`
}

const NENNT_SHOWCASE = /^(?:Ich stehe vor|I'm standing at|Erzähl mir von|Tell me about):\s*(.+)$/i

/** Der Showcase, um den es in der Antwort geht, falls er sich sicher bestimmen lässt. */
export function showcaseDerAntwort<
  T extends Omit<PartnerShowcase, 'partnerName' | 'partnerLogoKey'>,
>(
  antwort: { toolCalls?: { name: string; input?: Record<string, unknown> }[] },
  frage: string | undefined,
  showcases: T[],
): T | null {
  const geladen = [...(antwort.toolCalls ?? [])].reverse().find((c) => c.name === 'get_showcase')
    ?.input?.showcaseIdOrSlug
  if (typeof geladen === 'string') {
    const s = showcases.find((x) => x.id === geladen || x.slug === geladen)
    if (s) return s
  }
  const genannt = frage ? NENNT_SHOWCASE.exec(frage.trim())?.[1]?.trim().toLowerCase() : undefined
  if (!genannt) return null
  return showcases.find((x) => x.title.trim().toLowerCase() === genannt) ?? null
}
